import type {
  InformCustomerOperationsPreview,
  InformCustomerPreview,
  InformCustomerRecipientSummary,
  InformCustomerSourcePreview,
  InformCustomerSourceRowContext,
  InformPropertySummary,
  InformScheduleEventSummary,
  InformWorkOrderSummary,
} from "../contract"

type JsonObject = Record<string, unknown>

const objectValue = (value: unknown, path: string): JsonObject => {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error(`${path} muss ein Objekt sein.`)

  return value as JsonObject
}

const exactKeys = (value: JsonObject, allowed: readonly string[], path: string) => {
  const unexpected = Object.keys(value).filter((key) => !allowed.includes(key))

  if (unexpected.length)
    throw new Error(`${path} enthält unbekannte Felder: ${unexpected.join(", ")}.`)
}

const text = (value: unknown, path: string) => {
  if (typeof value !== "string" || !value.trim())
    throw new Error(`${path} muss ein nicht-leerer String sein.`)

  return value.trim()
}

const nullableText = (value: unknown, path: string) => {
  if (value === null || value === undefined) return ""

  if (typeof value !== "string") throw new Error(`${path} muss String oder null sein.`)

  return value.trim()
}

const nullableId = (value: unknown, path: string): string | null =>
  nullableText(value, path) || null

const integerOrNull = (value: unknown, path: string): number | null => {
  if (value === null || value === undefined) return null

  if (typeof value !== "number" || !Number.isInteger(value))
    throw new Error(`${path} muss Ganzzahl oder null sein.`)

  return value
}

const textArray = (value: unknown, path: string) => {
  if (!Array.isArray(value)) throw new Error(`${path} muss ein Array sein.`)

  return value.map((entry, index) => text(entry, `${path}.${index}`))
}

const workOrder = (value: unknown, path: string): InformWorkOrderSummary => {
  const data = objectValue(value, path)
  exactKeys(
    data,
    [
      "id",
      "property_id",
      "property_unit_id",
      "property_unit_label",
      "order_number",
      "order_type_ids",
      "order_type_label",
      "due_date",
      "estimated_duration_min",
      "assigned_employee_id",
      "allowed_employee_ids",
      "current_schedule_event_id",
      "status",
    ],
    path,
  )

  return {
    id: text(data.id, `${path}.id`),
    propertyId: text(data.property_id, `${path}.property_id`),
    propertyUnitId: nullableId(data.property_unit_id, `${path}.property_unit_id`),
    propertyUnitLabel: nullableText(data.property_unit_label, `${path}.property_unit_label`),
    orderNumber: nullableText(data.order_number, `${path}.order_number`),
    orderTypeIds: textArray(data.order_type_ids, `${path}.order_type_ids`),
    orderTypeLabel: nullableText(data.order_type_label, `${path}.order_type_label`),
    dueDate: nullableId(data.due_date, `${path}.due_date`),
    estimatedDurationMin: integerOrNull(
      data.estimated_duration_min,
      `${path}.estimated_duration_min`,
    ),
    assignedEmployeeId: nullableId(data.assigned_employee_id, `${path}.assigned_employee_id`),
    allowedEmployeeIds: textArray(data.allowed_employee_ids, `${path}.allowed_employee_ids`),
    currentScheduleEventId: nullableId(
      data.current_schedule_event_id,
      `${path}.current_schedule_event_id`,
    ),
    status: text(data.status, `${path}.status`),
  }
}

const property = (value: unknown, path: string): InformPropertySummary => {
  const data = objectValue(value, path)
  exactKeys(
    data,
    [
      "id",
      "street",
      "house_number",
      "postal_code",
      "city",
      "country",
      "address_extra",
      "address_label",
    ],
    path,
  )

  return {
    id: text(data.id, `${path}.id`),
    street: text(data.street, `${path}.street`),
    houseNumber: nullableText(data.house_number, `${path}.house_number`),
    postalCode: nullableText(data.postal_code, `${path}.postal_code`),
    city: nullableText(data.city, `${path}.city`),
    country: nullableText(data.country, `${path}.country`),
    addressExtra: nullableText(data.address_extra, `${path}.address_extra`),
    addressLabel: text(data.address_label, `${path}.address_label`),
  }
}

const event = (value: unknown, path: string): InformScheduleEventSummary => {
  const data = objectValue(value, path)
  exactKeys(
    data,
    [
      "id",
      "work_order_id",
      "property_id",
      "employee_id",
      "employee_name",
      "start_at",
      "end_at",
      "status",
      "flex_type",
      "timeframe_start_at",
      "timeframe_end_at",
      "customer_window_start_at",
      "customer_window_end_at",
    ],
    path,
  )

  return {
    id: text(data.id, `${path}.id`),
    workOrderId: nullableId(data.work_order_id, `${path}.work_order_id`),
    propertyId: nullableId(data.property_id, `${path}.property_id`),
    employeeId: nullableId(data.employee_id, `${path}.employee_id`),
    employeeName: nullableText(data.employee_name, `${path}.employee_name`),
    startAt: text(data.start_at, `${path}.start_at`),
    endAt: text(data.end_at, `${path}.end_at`),
    status: text(data.status, `${path}.status`),
    flexType: nullableId(data.flex_type, `${path}.flex_type`),
    timeframeStartAt: nullableId(data.timeframe_start_at, `${path}.timeframe_start_at`),
    timeframeEndAt: nullableId(data.timeframe_end_at, `${path}.timeframe_end_at`),
    customerWindowStartAt: nullableId(
      data.customer_window_start_at,
      `${path}.customer_window_start_at`,
    ),
    customerWindowEndAt: nullableId(data.customer_window_end_at, `${path}.customer_window_end_at`),
  }
}

const recipient = (value: unknown, path: string): InformCustomerRecipientSummary => {
  const data = objectValue(value, path)
  exactKeys(data, ["customer_id", "display_name", "role", "email", "phone", "phone_kind"], path)
  const phoneKind = nullableText(data.phone_kind, `${path}.phone_kind`).toUpperCase()

  if (phoneKind && phoneKind !== "MOBILE" && phoneKind !== "FIXED_LINE") {
    throw new Error(`${path}.phone_kind enthält einen unbekannten Kontaktpunkttyp.`)
  }

  return {
    customerId: text(data.customer_id, `${path}.customer_id`),
    displayName: text(data.display_name, `${path}.display_name`),
    role: nullableText(data.role, `${path}.role`),
    email: nullableText(data.email, `${path}.email`),
    phone: nullableText(data.phone, `${path}.phone`),
    phoneKind: (phoneKind || null) as "MOBILE" | "FIXED_LINE" | null,
  }
}

const keyed = <T>(
  value: unknown,
  path: string,
  parser: (entry: unknown, entryPath: string) => T,
  idFor: (entry: T) => string,
) =>
  Object.fromEntries(
    Object.entries(objectValue(value, path)).map(([id, entry]) => {
      const parsed = parser(entry, `${path}.${id}`)

      if (idFor(parsed) !== id) throw new Error(`${path}.${id} referenziert eine abweichende ID.`)

      return [id, parsed]
    }),
  )

const sourcePreview = (root: JsonObject): InformCustomerSourcePreview => {
  exactKeys(
    root,
    ["mode", "row_context_by_action_case_id", "row_errors_by_action_case_id", "shared"],
    "payload",
  )
  const rows = objectValue(
    root.row_context_by_action_case_id,
    "payload.row_context_by_action_case_id",
  )
  const errors = objectValue(
    root.row_errors_by_action_case_id,
    "payload.row_errors_by_action_case_id",
  )
  const shared = objectValue(root.shared, "payload.shared")
  exactKeys(
    shared,
    ["work_orders_by_id", "properties_by_id", "schedule_events_by_id", "recipients_by_id"],
    "payload.shared",
  )

  return {
    mode: "source",
    rowContextByActionCaseId: Object.fromEntries(
      Object.entries(rows).map(([id, value]) => {
        const row = objectValue(value, `payload.row_context_by_action_case_id.${id}`)
        exactKeys(
          row,
          [
            "work_order_id",
            "schedule_event_id",
            "property_id",
            "requester_customer_id",
            "requester_role",
            "target_customer_ids",
            "source_schedule_event_id",
            "canceled_schedule_event_id",
          ],
          `payload.row_context_by_action_case_id.${id}`,
        )

        return [
          id,
          {
            workOrderId: text(
              row.work_order_id,
              `payload.row_context_by_action_case_id.${id}.work_order_id`,
            ),
            scheduleEventId: text(
              row.schedule_event_id,
              `payload.row_context_by_action_case_id.${id}.schedule_event_id`,
            ),
            propertyId: text(
              row.property_id,
              `payload.row_context_by_action_case_id.${id}.property_id`,
            ),
            requesterCustomerId: text(
              row.requester_customer_id,
              `payload.row_context_by_action_case_id.${id}.requester_customer_id`,
            ),
            requesterRole: nullableText(
              row.requester_role,
              `payload.row_context_by_action_case_id.${id}.requester_role`,
            ),
            targetCustomerIds: textArray(
              row.target_customer_ids,
              `payload.row_context_by_action_case_id.${id}.target_customer_ids`,
            ),
            sourceScheduleEventId: nullableId(
              row.source_schedule_event_id,
              `payload.row_context_by_action_case_id.${id}.source_schedule_event_id`,
            ),
            canceledScheduleEventId: nullableId(
              row.canceled_schedule_event_id,
              `payload.row_context_by_action_case_id.${id}.canceled_schedule_event_id`,
            ),
          } satisfies InformCustomerSourceRowContext,
        ]
      }),
    ),
    rowErrorsByActionCaseId: Object.fromEntries(
      Object.entries(errors).map(([id, value]) => [
        id,
        text(value, `payload.row_errors_by_action_case_id.${id}`),
      ]),
    ),
    workOrdersById: keyed(
      shared.work_orders_by_id,
      "payload.shared.work_orders_by_id",
      workOrder,
      (entry) => entry.id,
    ),
    propertiesById: keyed(
      shared.properties_by_id,
      "payload.shared.properties_by_id",
      property,
      (entry) => entry.id,
    ),
    scheduleEventsById: keyed(
      shared.schedule_events_by_id,
      "payload.shared.schedule_events_by_id",
      event,
      (entry) => entry.id,
    ),
    recipientsById: keyed(
      shared.recipients_by_id,
      "payload.shared.recipients_by_id",
      recipient,
      (entry) => entry.customerId,
    ),
  }
}

const operationsPreview = (root: JsonObject): InformCustomerOperationsPreview => {
  exactKeys(root, ["mode", "previews", "failed", "cost_benefit"], "payload")

  if (!Array.isArray(root.previews)) throw new Error("payload.previews muss ein Array sein.")

  if (!Array.isArray(root.failed)) throw new Error("payload.failed muss ein Array sein.")

  const previews = root.previews.map((entry, index) =>
    objectValue(entry, `payload.previews.${index}`),
  )
  const failed = root.failed.map((entry, index) => objectValue(entry, `payload.failed.${index}`))
  const costBenefit =
    root.cost_benefit === null ? null : objectValue(root.cost_benefit, "payload.cost_benefit")

  return {
    mode: "operations",
    response: {
      previews: previews as InformCustomerOperationsPreview["response"]["previews"],
      failed: failed as InformCustomerOperationsPreview["response"]["failed"],
      cost_benefit: costBenefit as InformCustomerOperationsPreview["response"]["cost_benefit"],
    },
  }
}

export const parseInformCustomerPreview = (payload: unknown): InformCustomerPreview => {
  const root = objectValue(payload, "payload")
  const mode = text(root.mode, "payload.mode")

  if (mode === "source") return sourcePreview(root)

  if (mode === "operations") return operationsPreview(root)

  throw new Error(`payload.mode enthält den unbekannten Wert ${mode}.`)
}
